/*
 * /auth/massage-report — VAC massage billing report
 */
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Container,
	PageHeader,
	PageWithEffect,
	toast,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { Download, FileSpreadsheet, Loader2, RefreshCw } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { format } from "date-fns"
import { trpc } from "../../trpc"
import { rolePageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/auth/massage-report")({
	component: MassageReportPage,
	loader: rolePageLoader(["massage_therapist", "admin", "super_admin"]),
})

function MassageReportPage() {
	const { data: practitioners = [] } = trpc.practitioner.getAllByFilter.useQuery({ where: { active: true } })

	const [selectedTherapists, setSelectedTherapists] = useState<string[]>([])
	const [startDate, setStartDate] = useState("")
	const [endDate, setEndDate] = useState("")

	useEffect(() => {
		if ((practitioners as any[]).length > 0 && selectedTherapists.length === 0) {
			setSelectedTherapists((practitioners as any[]).map((p) => p.id))
		}
	}, [practitioners])

	const { data: jobs = [], refetch: refetchJobs } = trpc.massageSession.getBillingJobs.useQuery()

	const hasPending = (jobs as any[]).some(
		(j: any) => j.status === "pending" || j.status === "processing",
	)

	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const [countdown, setCountdown] = useState(30)

	useEffect(() => {
		if (!hasPending) {
			if (intervalRef.current) clearInterval(intervalRef.current)
			intervalRef.current = null
			setCountdown(30)
			return
		}
		let secs = 30
		setCountdown(secs)
		intervalRef.current = setInterval(() => {
			secs -= 1
			setCountdown(secs)
			if (secs <= 0) {
				refetchJobs()
				secs = 30
				setCountdown(secs)
			}
		}, 1000)
		return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
	}, [hasPending, refetchJobs])

	const queueReport = trpc.massageSession.queueBillingReport.useMutation({
		onSuccess: () => {
			toast.success("Report queued — check the history below")
			refetchJobs()
		},
		onError: (e) => toast.error(e.message),
	})

	function toggleTherapist(id: string) {
		setSelectedTherapists((prev) =>
			prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
		)
	}

	function handleGenerate() {
		if (!selectedTherapists.length || !startDate || !endDate) {
			toast.error("Select at least one therapist and a date range.")
			return
		}
		queueReport.mutate({ therapistIds: selectedTherapists, startDate, endDate })
	}

	return (
		<PageWithEffect>
			<Container>
				<PageHeader title="Massage Billing Report" />

				<Card className="mb-6">
					<CardHeader>
						<CardTitle className="text-base">Report Filters</CardTitle>
						<CardDescription>Select therapists and date range for the VAC billing report.</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						{/* Therapist multi-select */}
						<div>
							<p className="text-sm font-medium mb-2">Therapists</p>
							<div className="flex flex-wrap gap-2">
								{(practitioners as any[]).map((p) => {
									const isSelected = selectedTherapists.includes(p.id)
									return (
										<button
											key={p.id}
											onClick={() => toggleTherapist(p.id)}
											className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
												isSelected
													? "bg-primary text-primary-foreground border-primary"
													: "bg-background text-foreground border-border hover:border-primary"
											}`}
										>
											{p.firstName} {p.lastName}
										</button>
									)
								})}
								{!(practitioners as any[]).length && (
									<p className="text-sm text-muted-foreground">No practitioners found.</p>
								)}
							</div>
						</div>

						{/* Date range */}
						<div className="flex flex-wrap gap-4">
							<div>
								<label className="text-sm font-medium block mb-1">Start Date</label>
								<input
									type="date"
									value={startDate}
									onChange={(e) => setStartDate(e.target.value)}
									className="h-9 rounded-md border bg-background px-3 text-sm"
								/>
							</div>
							<div>
								<label className="text-sm font-medium block mb-1">End Date</label>
								<input
									type="date"
									value={endDate}
									onChange={(e) => setEndDate(e.target.value)}
									className="h-9 rounded-md border bg-background px-3 text-sm"
								/>
							</div>
						</div>

						<div className="flex gap-2 pt-1">
							<Button onClick={handleGenerate} disabled={queueReport.isPending}>
								{queueReport.isPending ? (
									<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Queuing…</>
								) : (
									<><FileSpreadsheet className="w-4 h-4 mr-2" /> Generate Report</>
								)}
							</Button>
						</div>
					</CardContent>
				</Card>

				{/* Report History */}
				<Card>
					<CardHeader>
						<div className="flex items-center justify-between">
							<div>
								<CardTitle>Report History</CardTitle>
								{hasPending && (
									<CardDescription className="flex items-center gap-1.5 mt-1">
										<RefreshCw className="w-3.5 h-3.5 animate-spin" />
										Auto-refreshing in {countdown}s
									</CardDescription>
								)}
							</div>
							<Button variant="ghost" size="sm" onClick={() => refetchJobs()}>
								<RefreshCw className="w-3.5 h-3.5 mr-1" />
								Refresh
							</Button>
						</div>
					</CardHeader>
					<CardContent>
						{!(jobs as any[]).length ? (
							<p className="text-sm text-muted-foreground">No reports yet. Generate your first report above.</p>
						) : (
							<div className="rounded-lg border divide-y">
								{(jobs as any[]).map((job) => (
									<BillingJobRow key={job.id} job={job} />
								))}
							</div>
						)}
					</CardContent>
				</Card>
			</Container>
		</PageWithEffect>
	)
}

function BillingJobRow({ job }: { job: any }) {
	const [downloading, setDownloading] = useState(false)
	const downloadQuery = trpc.massageSession.downloadBillingReport.useQuery(
		{ id: job.id },
		{ enabled: false },
	)

	const filters = (() => {
		try { return job.filters ? JSON.parse(job.filters) : {} } catch { return {} }
	})()

	async function handleDownload() {
		setDownloading(true)
		try {
			const result = await downloadQuery.refetch()
			if (result.data?.url) {
				const a = document.createElement("a")
				a.href = result.data.url
				a.download = `massage-billing-${format(new Date(job.createdAt), "yyyy-MM-dd-HHmm")}.xlsx`
				document.body.appendChild(a)
				a.click()
				document.body.removeChild(a)
			}
		} catch (e: any) {
			toast.error(e.message ?? "Download failed")
		} finally {
			setDownloading(false)
		}
	}

	return (
		<div className="flex items-center gap-3 px-4 py-3">
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-2 flex-wrap">
					<span className="text-sm font-medium">Massage Billing Report</span>
					<StatusBadge status={job.status} />
					{job.rowCount != null && (
						<span className="text-xs text-muted-foreground">{job.rowCount} row{job.rowCount !== 1 ? "s" : ""}</span>
					)}
				</div>
				<div className="flex items-center gap-3 mt-0.5 flex-wrap">
					<span className="text-xs text-muted-foreground">
						{job.createdAt ? format(new Date(job.createdAt), "MMM d, yyyy 'at' h:mm a") : "—"}
					</span>
					{filters.startDate && filters.endDate && (
						<span className="text-xs text-muted-foreground">
							· {filters.startDate} to {filters.endDate}
						</span>
					)}
					{job.status === "error" && job.errorMessage && (
						<span className="text-xs text-destructive">· {job.errorMessage}</span>
					)}
				</div>
			</div>
			<div className="shrink-0">
				{job.status === "complete" ? (
					<Button size="sm" variant="outline" onClick={handleDownload} disabled={downloading}>
						{downloading ? (
							<Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
						) : (
							<Download className="w-3.5 h-3.5 mr-1" />
						)}
						Download
					</Button>
				) : (job.status === "pending" || job.status === "processing") ? (
					<Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
				) : null}
			</div>
		</div>
	)
}

function StatusBadge({ status }: { status: string }) {
	const map: Record<string, { label: string; cls: string }> = {
		pending:    { label: "Pending",    cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
		processing: { label: "Processing", cls: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" },
		complete:   { label: "Complete",   cls: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300" },
		error:      { label: "Error",      cls: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" },
	}
	const v = map[status] ?? { label: status, cls: "bg-muted text-muted-foreground" }
	return (
		<span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${v.cls}`}>
			{v.label}
		</span>
	)
}
